'use client';

import React from 'react';
import { Project } from '@/domains/projects/schemas';
import { ProjectGroup as ProjectGroupType } from '@/domains/projects/schemas/project-groups';
import { CompactProjectCard } from './compact-project-card';
import { Button } from '@/components/ui/button';
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  MoreHorizontal,
  Pencil,
  Trash2,
  Plus,
  ArrowRightToLine,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';

interface ProjectGroupProps {
  group: ProjectGroupType;
  projects: Partial<Project>[];
  ungroupedProjects: Partial<Project>[];
  onToggleCollapse: (groupId: string) => void;
  onRenameGroup: (groupId: string) => void;
  onDeleteGroup: (groupId: string) => void;
  onReorderProjects: (groupId: string, newProjectIds: string[]) => void;
  onRemoveProject: (projectId: string, groupId: string) => void;
  onAddProject: (projectId: string, groupId: string) => void;
  onCreateProject: (groupId: string) => void;
  onDeleteProject: (project: Partial<Project>) => void;
  onDuplicateProject: (project: Partial<Project>) => void;
  onTransferProject?: (project: Partial<Project>) => void;
  /** Used as the sortable ID for top-level reordering */
  sortableId: string;
}

export function ProjectGroupContainer({
  group,
  projects,
  ungroupedProjects,
  onToggleCollapse,
  onRenameGroup,
  onDeleteGroup,
  onReorderProjects,
  onRemoveProject,
  onAddProject,
  onCreateProject,
  onDeleteProject,
  onDuplicateProject,
  onTransferProject,
  sortableId,
}: ProjectGroupProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = group.projectIds.indexOf(active.id as string);
    const newIndex = group.projectIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(group.projectIds, oldIndex, newIndex);
    onReorderProjects(group.id, newOrder);
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border bg-card/50 transition-all',
        isDragging && 'opacity-50'
      )}
    >
      {/* Group Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border/50">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0"
        >
          <GripVertical className="h-5 w-5" />
        </div>

        <button
          onClick={() => onToggleCollapse(group.id)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          {group.isCollapsed ? (
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
          <h3 className="font-semibold text-sm truncate">{group.name}</h3>
          <span className="text-xs text-muted-foreground shrink-0">
            ({projects.length})
          </span>
        </button>

        <div className="flex items-center gap-1 shrink-0">
          {/* Create New Project in Group */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onCreateProject(group.id)}
            title="Create new project in this group"
          >
            <Plus className="h-4 w-4" />
          </Button>

          {/* Group Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {ungroupedProjects.length > 0 && (
                <>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <ArrowRightToLine className="h-4 w-4 mr-2" />
                      Move Existing Project Here
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="max-h-60 overflow-y-auto">
                      {ungroupedProjects.map((p) => (
                        <DropdownMenuItem
                          key={p.id}
                          onClick={() => onAddProject(p.id!, group.id)}
                        >
                          <span className="truncate">{p.title}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => onRenameGroup(group.id)}>
                <Pencil className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDeleteGroup(group.id)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Group
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Group Content */}
      {!group.isCollapsed && (
        <div className="p-4">
          {projects.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No projects in this group yet. Use the + button to create a new project.
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={group.projectIds}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {projects.map((project) => (
                    <CompactProjectCard
                      key={project.id}
                      project={project}
                      onDelete={onDeleteProject}
                      onDuplicate={onDuplicateProject}
                      onTransfer={onTransferProject}
                      onRemoveFromGroup={(projectId) =>
                        onRemoveProject(projectId, group.id)
                      }
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
}
